const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
export const AUTH_EXPIRED_EVENT = 'auth:expired';
let accessToken = null;
let refreshPromise = null;

const handleUnauthorized = () => {
  accessToken = null;
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
};

const getAuthHeaders = (headers = {}) => ({
  ...headers,
  ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
});

const request = async (endpoint, options = {}, retryOnUnauthorized = true) => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: getAuthHeaders(options.headers),
    credentials: 'include'
  });

  if (response.status === 401 && retryOnUnauthorized && endpoint !== '/auth/refresh') {
    const refreshed = await refreshSession();
    if (refreshed) {
      return request(endpoint, options, false);
    }
    handleUnauthorized();
    return null;
  }

  return response;
};

export const setAccessToken = (token) => {
  accessToken = token || null;
};

export const clearAccessToken = () => {
  accessToken = null;
};

export const refreshSession = async () => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.status === 204) {
        accessToken = null;
        return null;
      }

      if (!response.ok) {
        accessToken = null;
        return null;
      }

      const data = await response.json();
      accessToken = data?.accessToken || null;
      return data;
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

export const api = {
  // Auth
  login: async (username, password) => {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.message || 'Erro ao fazer login');
    }
    accessToken = data?.accessToken || null;
    return data;
  },

  logout: async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } finally {
      accessToken = null;
    }
  },

  // Patients
  getPatients: async () => {
    const response = await request('/patients');
    if (!response) return [];
    return response.json();
  },

  createPatient: async (patientData) => {
    const response = await request('/patients', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(patientData)
    });
    if (!response) return;
    return response.json();
  },

  updatePatient: async (id, patientData) => {
    const response = await request(`/patients/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(patientData)
    });
    if (!response) return;
    return response.json();
  },

  getPatient: async (id) => {
    const response = await request(`/patients/${id}`);
    if (!response) return;
    return response.json();
  },

  // Appointments
  get: async (endpoint) => {
    const response = await request(endpoint);
    if (!response) return;
    if (!response.ok) {
      throw new Error('Erro ao buscar dados');
    }
    return { data: await response.json() };
  },

  post: async (endpoint, data) => {
    const response = await request(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response) return;
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }
    return { data: await response.json() };
  },

  put: async (endpoint, data) => {
    const response = await request(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response) return;
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }
    return { data: await response.json() };
  },

  delete: async (endpoint) => {
    const response = await request(endpoint, { method: 'DELETE' });
    if (!response) return;
    if (!response.ok) {
      throw new Error('Erro ao deletar');
    }
    return { data: await response.json() };
  },

  upload: async (endpoint, formData) => {
    // Não define Content-Type — o browser define automaticamente com o boundary correto
    const response = await request(endpoint, {
      method: 'POST',
      body: formData
    });
    if (!response) return;
    if (!response.ok) {
      const error = await response.json();
      throw { response: { data: error } };
    }
    return { data: await response.json() };
  }
};
